import React, { useState, useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { DateInput } from '../DateTimeInput';
import { ReadOnlyField } from '../FormRow';
import { FileText, Globe, Plus, Trash2, Phone, Mail, Upload, AlertCircle, CheckCircle2, ExternalLink, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { CBProgressStepper } from './CBProgressStepper';
import { documentAPI } from '../../api';

const REQUIRED_DOCS = [
  { key: 'ccp_layout', label: 'CCP Layout' },
  { key: 'cfdi', label: 'CFDI' },
  { key: 'w9', label: 'W9' },
  { key: 'doda', label: 'DODA' },
  { key: 'us_entry', label: 'US Entry' },
  { key: 'redo_documents', label: 'ReDo Documents' },
];

export const CrossBorderTab = ({
  load,
  setLoad,
  getFieldClass,
  isCarrierUser,
  cbDocuments,
  setShowAddDocModal,
  handleDeleteCBDocument,
  fetchLoadData,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const checkedDocs = REQUIRED_DOCS.filter((d) => load.crossing_docs?.[d.key]);
  const missingDocs = REQUIRED_DOCS.filter((d) => !load.crossing_docs?.[d.key]);
  const completePct = Math.round((checkedDocs.length / REQUIRED_DOCS.length) * 100);
  const allDone = missingDocs.length === 0;

  const dateFields = [
    { key: 'crossing_docs_received_date', label: 'Docs Received' },
    { key: 'crossing_docs_due_date', label: 'Docs Due' },
    { key: 'crossing_eta_date', label: 'Crossing ETA' },
    { key: 'border_arrival_date_mx', label: 'Border MX', region: 'mx' },
    { key: 'border_arrival_date_us', label: 'Border US', region: 'us' },
  ];

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let success = 0;
    let failed = 0;
    for (const file of files) {
      try {
        // Reject oversized files (>25MB)
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} is larger than 25MB`);
          failed += 1;
          continue;
        }
        await documentAPI.upload(file, 'load', load.id, 'cross_border', null);
        success += 1;
      } catch (e) {
        failed += 1;
      }
    }
    setUploading(false);
    if (success) toast.success(`Uploaded ${success} document${success > 1 ? 's' : ''}`);
    if (failed) toast.error(`${failed} upload${failed > 1 ? 's' : ''} failed`);
    if (success && fetchLoadData) await fetchLoadData();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (isCarrierUser) return;
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!isCarrierUser) setDragOver(true);
  };

  const handleFilePick = (e) => {
    uploadFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const brokerInitial = (load.transfer_name || '?').charAt(0).toUpperCase();

  return (
    <div className="space-y-4">
      {/* 1. Visual progress stepper */}
      <CBProgressStepper load={load} />

      {/* 2. Key dates strip - region-tinted */}
      <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4">
        <h3 className="text-sm font-semibold text-[var(--tms-text)] mb-3">KEY DATES</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {dateFields.map((f) => (
            <div
              key={f.key}
              className={`p-2 rounded border ${
                f.region === 'mx' ? 'border-orange-200 bg-orange-50/50' :
                f.region === 'us' ? 'border-sky-200 bg-sky-50/50' :
                'border-[var(--tms-border)]'
              }`}
            >
              <Label className="text-xs text-[var(--tms-text-muted)]">{f.label}</Label>
              {isCarrierUser ? (
                <ReadOnlyField value={load[f.key]} />
              ) : (
                <DateInput
                  value={load[f.key] || ''}
                  onChange={(e) => setLoad({ ...load, [f.key]: e.target.value || null })}
                  className="h-9 text-sm"
                  data-testid={`input-${f.key}`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 3. Three-column row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* === Transfer Broker Contact Card === */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-1.5">
            <Truck className="w-4 h-4" /> TRANSFER BROKER
          </h3>

          <div className="flex items-start gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center text-lg font-bold flex-shrink-0">
              {brokerInitial}
            </div>
            <div className="flex-1 min-w-0">
              {isCarrierUser ? (
                <p className="font-semibold text-sm truncate">{load.transfer_name || '—'}</p>
              ) : (
                <Input
                  value={load.transfer_name || ''}
                  onChange={(e) => setLoad({ ...load, transfer_name: e.target.value })}
                  className={`h-7 text-sm font-semibold bg-transparent border-amber-300 ${getFieldClass('transfer_name')}`}
                  placeholder="Broker name"
                  data-testid="input-transfer-name"
                />
              )}
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label className="text-[10px] text-amber-700">SCAC</Label>
                  {isCarrierUser ? (
                    <p className="text-xs font-mono">{load.transfer_scac || '—'}</p>
                  ) : (
                    <Input
                      value={load.transfer_scac || ''}
                      onChange={(e) => setLoad({ ...load, transfer_scac: e.target.value.toUpperCase() })}
                      maxLength={4}
                      className={`h-6 text-xs font-mono uppercase bg-transparent border-amber-300 ${getFieldClass('transfer_scac')}`}
                      placeholder="ABCD"
                      data-testid="input-transfer-scac"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-[10px] text-amber-700">CAAT</Label>
                  {isCarrierUser ? (
                    <p className="text-xs font-mono">{load.transfer_caat || '—'}</p>
                  ) : (
                    <Input
                      value={load.transfer_caat || ''}
                      onChange={(e) => setLoad({ ...load, transfer_caat: e.target.value })}
                      className={`h-6 text-xs font-mono bg-transparent border-amber-300 ${getFieldClass('transfer_caat')}`}
                      placeholder="CAAT"
                      data-testid="input-transfer-caat"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2 border-t border-amber-200 pt-2">
            <div className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-amber-700 flex-shrink-0" />
              {isCarrierUser ? (
                load.transfer_phone ? (
                  <a href={`tel:${load.transfer_phone}`} className="text-xs text-amber-900 hover:underline" data-testid="transfer-phone-link">
                    {load.transfer_phone}
                  </a>
                ) : (
                  <span className="text-xs text-amber-700/60">No phone</span>
                )
              ) : (
                <Input
                  value={load.transfer_phone || ''}
                  onChange={(e) => setLoad({ ...load, transfer_phone: e.target.value })}
                  className="h-6 text-xs bg-transparent border-amber-300"
                  placeholder="Phone"
                  data-testid="input-transfer-phone"
                />
              )}
              {!isCarrierUser && load.transfer_phone && (
                <a href={`tel:${load.transfer_phone}`} className="p-1 text-amber-700 hover:bg-amber-200 rounded" title="Call">
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-amber-700 flex-shrink-0" />
              {isCarrierUser ? (
                load.transfer_email ? (
                  <a href={`mailto:${load.transfer_email}`} className="text-xs text-amber-900 hover:underline truncate" data-testid="transfer-email-link">
                    {load.transfer_email}
                  </a>
                ) : (
                  <span className="text-xs text-amber-700/60">No email</span>
                )
              ) : (
                <Input
                  value={load.transfer_email || ''}
                  onChange={(e) => setLoad({ ...load, transfer_email: e.target.value })}
                  className="h-6 text-xs bg-transparent border-amber-300"
                  placeholder="Email"
                  data-testid="input-transfer-email"
                />
              )}
              {!isCarrierUser && load.transfer_email && (
                <a href={`mailto:${load.transfer_email}`} className="p-1 text-amber-700 hover:bg-amber-200 rounded" title="Email">
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* === Border Clearance Doc Checklist with progress bar === */}
        <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--tms-text)] flex items-center gap-1.5">
              {allDone ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-amber-600" />}
              BORDER CLEARANCE DOCS
            </h3>
            <span className={`text-xs font-semibold ${allDone ? 'text-emerald-600' : 'text-amber-700'}`}>
              {checkedDocs.length} / {REQUIRED_DOCS.length}
            </span>
          </div>

          <div className="w-full bg-[var(--tms-hover)] rounded-full h-2 mb-3 overflow-hidden">
            <div
              className={`h-full transition-all rounded-full ${allDone ? 'bg-emerald-500' : 'bg-amber-500'}`}
              style={{ width: `${completePct}%` }}
              data-testid="docs-progress-bar"
            />
          </div>

          {!allDone && (
            <div className="mb-3 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900" data-testid="missing-docs-banner">
              <strong>Missing:</strong> {missingDocs.map((d) => d.label).join(', ')}
            </div>
          )}
          {allDone && (
            <div className="mb-3 px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-900">
              <strong>Ready to clear customs ✓</strong>
            </div>
          )}

          <div className="space-y-1">
            {REQUIRED_DOCS.map((doc) => {
              const checked = load.crossing_docs?.[doc.key] || false;
              return (
                <button
                  key={doc.key}
                  type="button"
                  disabled={isCarrierUser}
                  onClick={() =>
                    !isCarrierUser &&
                    setLoad({
                      ...load,
                      crossing_docs: { ...load.crossing_docs, [doc.key]: !checked },
                    })
                  }
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left transition-colors ${
                    checked
                      ? 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                      : 'bg-[var(--tms-hover)] text-[var(--tms-text-secondary)] hover:bg-[var(--tms-border)]/50'
                  } ${isCarrierUser ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                  data-testid={`checkbox-${doc.key}`}
                >
                  <Checkbox checked={checked} disabled className="pointer-events-none" />
                  <span className="flex-1">{doc.label}</span>
                  {checked && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* === CB Documents with drop zone === */}
        <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--tms-text)]">CB DOCUMENTS</h3>
            {!isCarrierUser && (
              <Button size="sm" variant="outline" onClick={() => setShowAddDocModal && setShowAddDocModal(true)} data-testid="add-cb-document-button">
                <Plus className="w-3 h-3 mr-1" /> Add URL
              </Button>
            )}
          </div>

          {!isCarrierUser && (
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={() => setDragOver(false)}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-4 mb-3 cursor-pointer transition-all text-center ${
                dragOver
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-[var(--tms-border)] hover:border-blue-300 hover:bg-[var(--tms-hover)]'
              }`}
              data-testid="cb-doc-drop-zone"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFilePick}
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                data-testid="cb-doc-file-input"
              />
              <Upload className={`w-5 h-5 mx-auto mb-1 ${dragOver ? 'text-blue-500' : 'text-[var(--tms-text-muted)]'}`} />
              <p className="text-xs font-medium text-[var(--tms-text-secondary)]">
                {uploading ? 'Uploading...' : dragOver ? 'Drop to upload' : 'Drag files here or click to upload'}
              </p>
              <p className="text-[10px] text-[var(--tms-text-muted)] mt-0.5">PDF, JPG, PNG, DOC, XLS</p>
            </div>
          )}

          <div className="flex-1 overflow-y-auto max-h-60">
            {cbDocuments.length === 0 ? (
              <div className="text-center py-4">
                <FileText className="w-6 h-6 text-[var(--tms-text-muted)] mx-auto mb-1" />
                <p className="text-xs text-[var(--tms-text-muted)]">No documents uploaded</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {cbDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-2 border rounded bg-[var(--tms-hover)]"
                    data-testid={`cb-doc-${doc.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText className="w-4 h-4 text-[var(--tms-primary)] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{doc.file_name}</p>
                        <p className="text-[10px] text-[var(--tms-text-muted)]">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        data-testid={`view-cb-doc-${doc.id}`}
                      >
                        <Globe className="w-3 h-3" />
                      </a>
                      {!isCarrierUser && (
                        <button
                          onClick={() => handleDeleteCBDocument(doc.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          data-testid={`delete-cb-doc-${doc.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossBorderTab;
